// src/Pages/faq/FAQPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { db } from "../../firebase/config";
import { Search, ChevronDown, MessageCircle } from "lucide-react";
import useConfigSitio from "../../hooks/useConfigSitio";

const WHATSAPP_TEXT = encodeURIComponent(
  "Hola 👋, tengo una consulta que no aparece en las preguntas frecuentes."
);

function Accordion({ question, answer, isOpen, onToggle }) {
  return (
    <div className="border rounded-lg">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between text-left px-4 py-3"
      >
        <span className="font-medium text-gray-900">{question}</span>
        <ChevronDown
          size={18}
          className={`transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </button>
      {isOpen && (
        <div className="px-4 pb-4 text-gray-700 whitespace-pre-wrap">
          {answer}
        </div>
      )}
    </div>
  );
}

export default function FAQPage() {
  // 🔹 Config global (whatsapp dinámico)
  const { config, loading } = useConfigSitio();

  const [cats, setCats] = useState([]); // categorías
  const [items, setItems] = useState([]); // preguntas de la categoría
  const [catId, setCatId] = useState("");
  const [queryText, setQueryText] = useState("");
  const [open, setOpen] = useState(null);

  // ========= WHATSAPP DINÁMICO =========
  // Fallback por si todavía no carga config o está vacío
  let waNumber = "56976604702";
  let waDisplay = "+56 9 7660 4702";

  if (!loading && config?.whatsapp) {
    // De algo tipo "+56 9 7660 4702" saco solo dígitos: 56976604702
    const digits = config.whatsapp.replace(/\D/g, "");
    if (digits) {
      waNumber = digits;
      waDisplay = config.whatsapp; // se muestra tal cual lo escribes en admin
    }
  }

  const waUrl = `https://wa.me/${waNumber}?text=${WHATSAPP_TEXT}`;

  // ========= Suscripción categorías =========
  useEffect(() => {
    const qCats = query(
      collection(db, "faq_categories"),
      orderBy("order", "asc")
    );

    const unsub = onSnapshot(qCats, (snap) => {
      const arr = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
      setCats(arr);
      if (!catId && arr.length) setCatId(arr[0].id);
    });

    return () => unsub();
  }, [catId]);

  // ========= Suscripción preguntas de la categoría seleccionada =========
  useEffect(() => {
    if (!catId) return;

    const qItems = query(
      collection(db, "faq_categories", catId, "items"),
      orderBy("order", "asc")
    );

    const unsub = onSnapshot(qItems, (snap) => {
      const arr = snap.docs
        .map((d) => ({ id: d.id, ...(d.data() || {}) }))
        .filter((i) => i.active !== false);
      setItems(arr);
      setOpen(null);
    });

    return () => unsub();
  }, [catId]);

  // ========= Filtro por buscador =========
  const filtered = useMemo(() => {
    const q = queryText.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (it) =>
        it.q?.toLowerCase().includes(q) || it.a?.toLowerCase().includes(q)
    );
  }, [items, queryText]);

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-10">
        <header className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-blue-900">
            Preguntas Frecuentes
          </h1>
          <p className="text-gray-600 mt-1">
            Encuentra respuestas rápidas. Si no aparece tu pregunta, escríbenos
            por WhatsApp.
          </p>
        </header>

        <div className="grid md:grid-cols-[220px_1fr] gap-6">
          {/* Sidebar categorías (desktop) */}
          <aside className="hidden md:block">
            <nav className="flex flex-col gap-1">
              {cats.map((c) => (
                <button
                  key={c.id}
                  onClick={() => {
                    setCatId(c.id);
                    setQueryText("");
                    setOpen(null);
                  }}
                  className={`text-left px-3 py-2 rounded-lg ${
                    c.id === catId
                      ? "bg-blue-600 text-white"
                      : "hover:bg-gray-100"
                  }`}
                >
                  {c.title}
                </button>
              ))}
            </nav>
          </aside>

          {/* Contenido */}
          <main>
            {/* Selector categorías (mobile) */}
            <div className="md:hidden mb-3">
              <select
                className="w-full border rounded-lg px-3 py-2"
                value={catId}
                onChange={(e) => {
                  setCatId(e.target.value);
                  setQueryText("");
                  setOpen(null);
                }}
              >
                {cats.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.title}
                  </option>
                ))}
              </select>
            </div>

            {/* Buscador */}
            <div className="relative mb-4">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2"
                size={18}
              />
              <input
                className="w-full pl-10 pr-3 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-200"
                placeholder="Buscar una pregunta…"
                value={queryText}
                onChange={(e) => setQueryText(e.target.value)}
              />
            </div>

            {/* Lista FAQs */}
            <div className="space-y-3">
              {filtered.length === 0 ? (
                <div className="text-gray-500 border rounded-lg p-4">
                  No encontramos resultados en esta categoría. Prueba otra
                  palabra o contáctanos por WhatsApp.
                </div>
              ) : (
                filtered.map((it, idx) => (
                  <Accordion
                    key={it.id}
                    question={it.q}
                    answer={it.a}
                    isOpen={open === idx}
                    onToggle={() =>
                      setOpen((o) => (o === idx ? null : idx))
                    }
                  />
                ))
              )}
            </div>

            {/* CTA WhatsApp */}
            <div className="mt-6 p-4 border rounded-xl bg-green-50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="text-green-900">
                <div className="font-semibold">¿No resolviste tu duda?</div>
                <div className="text-sm">
                  Escríbenos por WhatsApp y te ayudamos.
                </div>
              </div>
              <a
                href={waUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700"
              >
                <MessageCircle size={18} />
                {`WhatsApp ${waDisplay}`}
              </a>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
